import {Map as GeomaticoMap} from '@geomatico/geocomponents';
import {useMediaQuery} from '@mui/material';
import {lighten} from '@mui/system/colorManipulator';
import bbox from '@turf/bbox';
import circle from '@turf/circle';
import type {MultiPolygon} from 'geojson';
import type {ExpressionSpecification, FilterSpecification, LayerSpecification, MapLayerMouseEvent} from 'maplibre-gl';
import {type FC, useMemo, useRef, useState} from 'react';
// @ts-expect-error
import type {MapRef} from 'react-map-gl';
import InventoryPlotPopup from '@/components/ForestMassDetail/InventoryPlotPopup';
import {INITIAL_MAPSTYLE_URL, INITIAL_VIEWPORT} from '@/config';
import type {ForestMassSpecies} from '@/domain/entities/ForestMassSpecies';
import type {InventoryPlot} from '@/domain/entities/InventoryPlot';
import type {Species} from '@/domain/entities/Species';
import {getGeoJsonFromBoundary} from '@/domain/utils/getGeoJsonFromBoundary';
import {getGeoJsonFromInventoryPlots, type InventoryPlotMapFeature} from '@/domain/utils/getGeoJsonFromInventoryPlots';
import {primaryColor, secondaryColor} from '@/theme/theme';

// TYPES
type Viewport = {
  zoom: number;
  latitude: number;
  longitude: number;
  bearing: number;
  pitch: number;
};

type Props = {
  species: Array<Species>;
  forestMassSpecies?: Array<ForestMassSpecies>;
  inventoryPlots?: Array<InventoryPlot>;
  boundary: MultiPolygon;
  selectedInventoryPlotUuid?: string;
  mapStyle?: string;
  onInventoryPlotClick: (uuid: string) => void;
};

const MapDetail: FC<Props> = ({
  species,
  forestMassSpecies = [],
  selectedInventoryPlotUuid,
  inventoryPlots,
  boundary,
  mapStyle = INITIAL_MAPSTYLE_URL,
  onInventoryPlotClick,
}) => {
  const mapRef = useRef<MapRef>(null);
  const widescreen = useMediaQuery('(min-width: 900px)', {noSsr: true});
  const geojsonBoundary = getGeoJsonFromBoundary(boundary);

  const geojsonInventoryPlots = inventoryPlots && getGeoJsonFromInventoryPlots(inventoryPlots, forestMassSpecies);

  const [viewport, setViewport] = useState<Viewport>(INITIAL_VIEWPORT);
  const [clickedFeature, setClickedFeature] = useState<InventoryPlotMapFeature | undefined>(undefined);
  const [hoveredFeature, setHoveredFeature] = useState<InventoryPlotMapFeature | undefined>(undefined);

  const speciesColorExpression = useMemo(() => createSpeciesColorExpression(species), [species]);

  const feature = {
    type: 'Feature',
    geometry: boundary,
    properties: {},
  };

  const zoomToFeature = () => {
    if (feature && mapRef.current) {
      const [minX, minY, maxX, maxY] = bbox(geojsonBoundary);

      mapRef.current.fitBounds(
        [
          [minX, minY],
          [maxX, maxY],
        ],
        {
          duration: 1000,
          padding: {
            top: widescreen ? 120 : 90,
            right: 50,
            bottom: widescreen ? 120 : 90,
            left: 50,
          },
        },
      );
    }
  };

  const selectedInventoryPlot = useMemo(() => {
    if (selectedInventoryPlotUuid && inventoryPlots?.length) {
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: inventoryPlots?.find((ip) => ip.uuid === selectedInventoryPlotUuid)?.position
            .coordinates as number[],
        },
        properties: {
          diameter: inventoryPlots?.find((ip) => ip.uuid === selectedInventoryPlotUuid)?.diameter || 0,
        },
      };
    }
  }, [inventoryPlots, selectedInventoryPlotUuid]);

  const scopeInventoryPlot = useMemo(() => {
    if (selectedInventoryPlot) {
      return circle(selectedInventoryPlot?.geometry.coordinates, selectedInventoryPlot?.properties.diameter / 2, {
        steps: 64,
        units: 'meters',
      });
    }
  }, [selectedInventoryPlot]);

  const handleClick = (e: MapLayerMouseEvent) => {
    const feature = e.features?.[0];

    if (!feature || !inventoryPlots) {
      setClickedFeature(undefined);
      return;
    }
    if (feature) {
      setClickedFeature(feature as unknown as InventoryPlotMapFeature);
    }
  };

  const handleMouseMove = (e: MapLayerMouseEvent) => {
    const feature = e.features?.[0];

    if (!feature) {
      setHoveredFeature(undefined);
      return;
    }
    if (feature) {
      setHoveredFeature(feature as unknown as InventoryPlotMapFeature);
    }
  };

  const sources = {
    boundary: {
      type: 'geojson' as const,
      data: geojsonBoundary,
    },
    ...(scopeInventoryPlot && {
      scopeInventoryPlot: {
        type: 'geojson' as const,
        data: scopeInventoryPlot,
      },
    }),
    ...(geojsonInventoryPlots && {
      inventoryPlots: {
        type: 'geojson' as const,
        data: geojsonInventoryPlots,
      },
    }),
  };

  const layers: Array<LayerSpecification> = [
    {
      id: 'boundary-fill',
      type: 'fill',
      source: 'boundary',
      paint: {
        'fill-color': lighten(secondaryColor, 0.9),
        'fill-opacity': 0.3,
      },
    },
    {
      id: 'boundary-line',
      type: 'line',
      source: 'boundary',
      paint: {
        'line-color': secondaryColor,
        'line-width': 2,
      },
    },
    ...(geojsonInventoryPlots
      ? [
          {
            id: 'inventoryPlot',
            type: 'circle',
            source: 'inventoryPlots',
            filter: selectedInventoryPlotUuid
              ? (['!=', ['get', 'uuid'], selectedInventoryPlotUuid] as FilterSpecification)
              : ['all'],
            paint: {
              'circle-color': speciesColorExpression,
              'circle-opacity': 1,
              'circle-stroke-width': 1,
            },
          } satisfies LayerSpecification,
        ]
      : []),
    ...(selectedInventoryPlotUuid
      ? [
          {
            id: 'selectedInventoryPlots-circle',
            type: 'circle' as const,
            source: 'inventoryPlots',
            filter: ['==', ['get', 'uuid'], selectedInventoryPlotUuid] as FilterSpecification,
            paint: {
              'circle-radius': 8,
              'circle-color': primaryColor,
            },
          },
          {
            id: 'scopeInventoryPlot-line',
            type: 'line' as const,
            source: 'scopeInventoryPlot',
            paint: {
              'line-color': primaryColor,
              'line-width': 2,
              'line-dasharray': [2, 2],
            },
          },
          {
            id: 'scopeInventoryPlot-fill',
            type: 'fill' as const,
            source: 'scopeInventoryPlot',
            paint: {
              'fill-color': primaryColor,
              'fill-opacity': 0.3,
            },
          },
        ]
      : []),
  ];

  return (
    <div style={{height: '100%', width: '100%', position: 'relative'}}>
      <GeomaticoMap
        ref={mapRef}
        onLoad={zoomToFeature}
        cursor={hoveredFeature ? 'pointer' : 'auto'}
        sources={sources}
        layers={layers}
        mapStyle={mapStyle}
        scrollZoom={true}
        viewport={viewport}
        onViewportChange={setViewport}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        interactiveLayerIds={geojsonInventoryPlots ? ['inventoryPlot'] : []}
      >
        {clickedFeature && (
          <InventoryPlotPopup
            species={species}
            inventoryPlotMapFeature={clickedFeature}
            onClick={onInventoryPlotClick}
            onClose={() => setClickedFeature(undefined)}
          />
        )}
      </GeomaticoMap>
    </div>
  );
};

export default MapDetail;

const createSpeciesColorExpression = (species: Species[]): ExpressionSpecification => {
  const expression: (string | string[])[] = ['match', ['get', 'speciesUuid']];

  for (const {uuid, color} of species) {
    expression.push(uuid, color);
  }

  expression.push('#000000');

  return expression as ExpressionSpecification;
};
